import { createSelector } from "reselect";

import ChatVoicePhase from "@mapmycustomers/shared/enum/chat/ChatVoicePhase";

import { RootState } from "@app/store/rootReducer";

const chatVoiceState = (state: RootState) => state.chat.voice;

export const getChatVoicePhase = createSelector(chatVoiceState, ({ phase }) => phase);
export const getChatVoiceThreadKey = createSelector(chatVoiceState, ({ threadKey }) => threadKey);
export const getChatVoiceHint = createSelector(chatVoiceState, ({ hint }) => hint);
export const getChatVoiceError = createSelector(chatVoiceState, ({ error }) => error);
export const getChatVoiceElapsedMs = createSelector(chatVoiceState, ({ elapsedMs }) => elapsedMs);
export const getChatVoiceSegmentStartedAt = createSelector(
  chatVoiceState,
  ({ segmentStartedAt }) => segmentStartedAt
);

export const isChatVoiceSessionActive = createSelector(
  getChatVoicePhase,
  (phase) => phase !== ChatVoicePhase.IDLE
);

// Exported (not module-private) because Task 11's drain loop selects the raw parts map to count
// filled slots, outside this module.
export const getChatVoiceParts = createSelector(chatVoiceState, ({ parts }) => parts);

// The settled prefix only: seq 0 up to the first slot still pending. Slots fix the FINAL order;
// they do not fix the LIVE one. Joining every filled slot would insert a late-arriving early clip
// in front of text already on screen, so the rep watches their own words rewrite themselves.
// Rendering the prefix means text only ever appends.
const settledPrefix = (parts: Record<number, string>): { blocked: boolean; text: string } => {
  const pieces: string[] = [];
  for (let seq = 0; ; seq++) {
    const part = parts[seq];
    if (part === undefined) {
      // A hole means a clip is still in flight — but only if something after it has landed.
      // Slots 0..seq-1 are all present by construction (the loop would have exited earlier
      // otherwise), so any key beyond seq is a slot sitting past the prefix. Comparing against
      // pieces.length instead would false-positive on every empty-string slot already consumed.
      return { blocked: Object.keys(parts).length > seq, text: pieces.join(" ") };
    }
    if (part !== "") {
      pieces.push(part);
    }
  }
};

export const getChatVoiceTranscript = createSelector(
  getChatVoiceParts,
  (parts) => settledPrefix(parts).text
);

// Drives the trailing ellipsis, so a blocked prefix reads as waiting rather than as finished.
export const isChatVoiceTranscriptPending = createSelector(
  getChatVoiceParts,
  (parts) => settledPrefix(parts).blocked
);
